/**
 * Canonical id minting for lookup values.
 *
 * Every value in a `lookup_*` collection lives in the collection doc's
 * `values[]` array and carries an `id` of the form `<slug>_<seed|timestamp>`
 * (e.g. `swatch_group_seed`, `festina_group_1779803517846`). The id is
 * generated by the admin UI, never typed by hand.
 *
 * That convention used to be enforced only by whoever called
 * `addLookupValue` — the Lookup Manager's "Value (internal key)" box is free
 * text, so a display name typed there ("Dartmouth Brands") became the id
 * verbatim, spaces included. Minting now goes through `buildLookupValueId`,
 * which slugifies first and refuses anything outside `[a-z0-9_-]`.
 *
 * Hyphens are deliberately preserved: the seeded vocabulary already uses them
 * (`rose-gold`, `power-reserve`, `sub-seconds`), so folding them to
 * underscores here would fork the keys from the catalogue.
 */

/** Characters a lookup value id is allowed to contain. */
const SAFE_ID = /^[a-z0-9_-]+$/;

/**
 * Fold a display value into an internal lookup key: lowercase, with every run
 * of unsupported characters collapsed to a single underscore.
 *
 * "Dartmouth Brands" -> "dartmouth_brands"; "rose-gold" -> "rose-gold".
 */
export function slugifyLookupValue(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '_')
    .replace(/^[_-]+|[_-]+$/g, '');
}

/** True when `raw` is already a usable internal key (no normalisation needed). */
export function isValidLookupKey(raw: string): boolean {
  return SAFE_ID.test(raw);
}

export interface BuildLookupValueIdOptions {
  /** Timestamp suffix; injectable so tests don't race the clock. */
  now?: number;
  /**
   * Fail loud instead of normalising silently. Defaults to the Vite dev flag:
   * a bad key is a bug in the calling screen, so it should surface locally and
   * never block an admin mid-edit in production.
   */
  strict?: boolean;
}

/**
 * Build the `<slug>_<timestamp>` id for a new lookup value.
 *
 * Throws when the key is empty, or — in dev / strict mode — when the key
 * needed normalising, so the offending screen gets fixed rather than quietly
 * writing a second-class id.
 */
export function buildLookupValueId(
  rawValue: string,
  options: BuildLookupValueIdOptions = {},
): string {
  const {
    now = Date.now(),
    strict = import.meta.env.DEV,
  } = options;

  const raw = (rawValue ?? '').trim();
  const slug = slugifyLookupValue(raw);

  if (!slug) {
    throw new Error(
      `Cannot mint a lookup value id from "${rawValue}" — the internal key is empty once slugified.`,
    );
  }

  if (!isValidLookupKey(raw)) {
    const message =
      `Lookup value key "${raw}" is not a valid internal key ` +
      `(ids must match [a-z0-9_-]); normalised to "${slug}". ` +
      'Slugify the key in the calling screen before saving.';
    if (strict) throw new Error(message);
    console.warn(message);
  }

  return `${slug}_${now}`;
}
